import { useEffect, useState } from "react";
import { fetchComments, addComment } from "../services/firestore";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import "./Comments.css";

function timeAgo(timestamp) {
  if (!timestamp) return "just now";
  const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
  const diff = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

export default function Comments({ postId }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    fetchComments(postId)
      .then(setComments)
      .finally(() => setLoading(false));
  }, [postId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const commentName = user ? user.email.split("@")[0] : name.trim();
    if (!commentName) {
      setError("Please enter your name.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      await addComment(postId, { name: commentName, text: text.trim() });
      const updated = await fetchComments(postId);
      setComments(updated);
      setText("");
      if (!user) setName("");
    } catch {
      setError("Failed to post comment. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="comments-section">
      <h2 className="comments-title">Comments ({comments.length})</h2>

      <div className="comment-form-wrap">
        <h3 className="comment-form-heading">Leave a Comment</h3>
        {!user && (
          <p className="comment-login-hint">
            <Link to="/login">Login</Link> to comment with your account, or
            enter a name below.
          </p>
        )}
        <form className="comment-form" onSubmit={handleSubmit}>
          {!user && (
            <input
              type="text"
              placeholder="Your name *"
              className="comment-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          )}
          <textarea
            placeholder="Write your comment..."
            className="comment-textarea"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={4}
            required
          />
          {error && <p className="comment-error">{error}</p>}
          <button type="submit" className="comment-submit" disabled={submitting}>
            {submitting ? "Posting..." : "Post Comment"}
          </button>
        </form>
      </div>

      <div className="comments-list">
        {loading ? (
          <p className="comments-loading">Loading comments...</p>
        ) : comments.length === 0 ? (
          <p className="comments-empty">
            No comments yet. Be the first to comment!
          </p>
        ) : (
          comments.map((c) => (
            <div key={c.id} className="comment-item">
              <div className="comment-avatar">
                {c.name ? c.name[0].toUpperCase() : "?"}
              </div>
              <div className="comment-content">
                <div className="comment-header">
                  <strong className="comment-name">{c.name}</strong>
                  <span className="comment-time">{timeAgo(c.timestamp)}</span>
                </div>
                <p className="comment-text">{c.text}</p>
              </div>
            </div>
          ))
        )}
      </div>
    </section>
  );
}
